import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import {
    DEFAULT_CURRENCY_CODE,
    InjectionToken,
    LOCALE_ID,
    type EnvironmentProviders,
    type Provider,
} from '@angular/core';
import { provideClientHydration, withEventReplay, withIncrementalHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling, withPreloading, withRouterConfig, type Routes } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { SeoEffects } from '../+state/seo/seo.effects';
import { HyundaiPreloadingStrategy, withPreloadingToken } from '../utilities/preload';
import { provideCloudflareImageConfig } from './cloudflare.provider';
import { provideCloudinaryImageConfig } from './cloudinary.provider';
import { provideEnvironmentSpecificProviders } from './env-specific-providers/providers';

export const isE2e = new InjectionToken<boolean>('isE2e');

export interface CoreProviderConfig {
    routes: Routes;
    cloudinaryBaseUrl: string;
    cloudflareBaseUrl: string;
    e2e: boolean;
}

export function provideCore(config: CoreProviderConfig): Array<Provider | EnvironmentProviders> {
    return [
        provideClientHydration(withEventReplay(), withIncrementalHydration()),
        provideCloudinaryImageConfig({ baseUrl: config.cloudinaryBaseUrl }),
        provideCloudflareImageConfig(config.cloudflareBaseUrl),
        provideRouter(
            config.routes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
            withPreloading(HyundaiPreloadingStrategy),
            withRouterConfig({ onSameUrlNavigation: 'reload' }),
        ),
        provideStore({ router: routerReducer }),
        provideRouterStore(),
        provideAnimationsAsync(),
        provideEffects([SeoEffects]),
        provideEnvironmentSpecificProviders(),
        {
            provide: withPreloadingToken,
            useValue: !config.e2e,
        },
        { provide: isE2e, useValue: config.e2e },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useValue: 'EUR',
        },
        {
            provide: LOCALE_ID,
            useFactory: () => {
                registerLocaleData(localeDe, 'de-DE', localeDeExtra);
                return 'de-DE';
            },
        },
    ];
}
